/**
 * 操作履歴を画面に出すときの整え（表示のためだけの計算）。
 *
 * **内部の番号（英数字の羅列）と英字の記号を画面に出さない**ための1か所です（総点検 J-06・M3）。
 * 人はお名前（サーバが一緒に返す `names`）、トークは名前と種類、操作や状態は日本語の言葉にします。
 * 名簿で引けない方（別の会社の方の番号など）は「名簿にない方」と出し、番号は出しません。
 */
import type { AuditLogItem, AuditTarget } from '../api/types.js';
import { channelTypeLabel, postPolicyLabel, readReceiptLabel } from '../channels/labels.js';
import { auditTargetTypeLabel } from './action-labels.js';

import { formatDateTime } from '../format/datetime.js';

export { formatDateTime };

/** 内部の番号（UUID）の形。 */
const UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

/** 名簿で引けなかった方の言い方。 */
export const UNKNOWN_PERSON = 'メンバーに登録されていない方';

/** 内部IDからお名前（「高橋 誠一さん」）。引けなければ「名簿にない方」。 */
export function personLabel(
  userId: string | null | undefined,
  names: Record<string, string> | undefined,
): string {
  if (!userId) return UNKNOWN_PERSON;
  const name = names?.[userId] ?? names?.[userId.toLowerCase()];
  return name ? `${name}さん` : UNKNOWN_PERSON;
}

/**
 * 操作した方が空でも、自動ではなく**サーバを預かる担当がコマンドで**行った操作（#358 #368）。
 * 「自動の処理」と出すと、人が控えを見た・数え直したことが読み取れないため
 */
const SERVER_COMMAND_ACTIONS = new Set(['login.counts_reset', 'verification_sms.viewed']);

/** 操作した方。仕組みが自動で行った操作は、そう書く。 */
export function actorLabel(
  item: Pick<AuditLogItem, 'actorUserId' | 'actorDisplayName'> & { action?: string },
  names: Record<string, string> | undefined,
): string {
  if (item.actorUserId === null) {
    return item.action !== undefined && SERVER_COMMAND_ACTIONS.has(item.action)
      ? 'サーバの担当（コマンド）'
      : '自動の処理';
  }
  if (item.actorDisplayName) return `${item.actorDisplayName}さん`;
  return personLabel(item.actorUserId, names);
}

/** 文の中の内部の番号を、お名前に置き換える（「参加者に追加: <番号>」など）。 */
function replaceIds(text: string, names: Record<string, string> | undefined): string {
  return text.replace(UUID, (id) => personLabel(id, names));
}

/** 状態の記号を言葉にする（AI先輩の答え）。 */
const KNOWLEDGE_STATUS: Record<string, string> = {
  draft: '承認待ち',
  active: '使われています',
  disabled: '取り下げ済み',
};

/** AI先輩の答えの種類・出どころ。 */
const KNOWLEDGE_KIND: Record<string, string> = {
  qa: 'Q&A',
  doc: '公式資料',
  taught: '「AI先輩に教える」から',
};

/**
 * トークの設定の値（サーバは機械の値のまま記録する）を、画面と同じ言葉にする（#333）。
 * 項目の名前ごとに、トークの詳細の画面の選択肢と同じ言い換え（channels/labels.ts）を使う。
 * 古い記録の項目名（「投稿できる方」）も同じ扱いにする。
 */
const SETTING_VALUE_LABELS: Record<string, (value: string) => string | undefined> = {
  メッセージを送れる方: (value) =>
    value === 'members' || value === 'admins' ? postPolicyLabel(value) : undefined,
  投稿できる方: (value) =>
    value === 'members' || value === 'admins' ? postPolicyLabel(value) : undefined,
  既読の見せ方: (value) =>
    value === 'count' || value === 'none' ? readReceiptLabel(value) : undefined,
};

/** どの項目か分からないときにも、英字の内部の値をそのまま出さないための言い換え。 */
const KNOWN_VALUE_LABELS: Record<string, string> = {
  members: postPolicyLabel('members'),
  admins: postPolicyLabel('admins'),
  count: readReceiptLabel('count'),
  none: readReceiptLabel('none'),
  true: 'する',
  false: 'しない',
};

function settingValue(
  label: string,
  value: string,
  names: Record<string, string> | undefined,
): string {
  const trimmed = value.trim();
  const byField = SETTING_VALUE_LABELS[label]?.(trimmed);
  if (byField !== undefined) return byField;
  // 名前は書かれたとおりに出す（「none」という名前のトークを言い換えないように）
  const known = label === '名前' ? undefined : KNOWN_VALUE_LABELS[trimmed];
  if (known !== undefined) return known;
  return withoutHonorificForAssistant(replaceIds(value, names));
}

/**
 * 人以外（AI先輩）に「さん」を付けない（#333）。以前の記録には「AI先輩さん」と
 * 残っているものがあるため、読むときに直す（記録そのものは書き換えない・docs/01 15-2）。
 */
function withoutHonorificForAssistant(text: string): string {
  return text.replace(/AI先輩さん/g, 'AI先輩');
}

/**
 * 名簿に合わせた自動参加の記録（「名簿に合わせて参加: 2名」）を、
 * 「自動で参加 2名・自動で退出 1名」の形にする（#334 L-11）。当てはまらなければ null。
 */
function describeAutoMembership(from: string, to: string): string | null {
  const joined = /^名簿に合わせて参加: (\d+)名$/.exec(to);
  const left = /^名簿に合わせて退出: (\d+)名$/.exec(from);
  if ((to !== '' && !joined) || (from !== '' && !left) || (!joined && !left)) return null;
  const parts: string[] = [];
  if (joined) parts.push(`自動で参加 ${joined[1]}名`);
  if (left) parts.push(`自動で退出 ${left[1]}名`);
  return parts.join('・');
}

/** 変更の前後をつなぐ。 */
function change(from: unknown, to: unknown): string {
  // 空のまま「（ → 千葉支店）」と出さない（#621）
  const side = (value: unknown) => String(value ?? '') || 'なし';
  return `${side(from)} → ${side(to)}`;
}

/** 「名前=A / 投稿できる方=B」の形を「名前：A → C」のように前後で組にする。 */
function describeFieldChanges(
  from: string,
  to: string,
  names: Record<string, string> | undefined,
): string {
  const parse = (text: string): [string, string][] =>
    text
      .split(' / ')
      .filter((part) => part.length > 0)
      .map((part) => {
        const at = part.indexOf('=');
        return at < 0 ? [part, ''] : [part.slice(0, at), part.slice(at + 1)];
      });
  const before = parse(from);
  const after = parse(to);
  if (before.length > 0 && before.length === after.length) {
    return before
      .map(([label, value], index) => {
        const next = after[index]?.[1] ?? '';
        return `${label}：${settingValue(label, value, names)} → ${settingValue(label, next, names)}`;
      })
      .join('、');
  }
  return replaceIds(change(from, to), names);
}

/** 「参加者に追加: 〜」「投稿 4 件 / 添付 0 件」のような1つだけの文を整える。 */
function describeNote(text: string, names: Record<string, string> | undefined): string {
  const purge = /^投稿 (\d+) 件 \/ 添付 (\d+) 件$/.exec(text);
  if (purge) return `メッセージ${purge[1]}件・写真などのファイル${purge[2]}件`;
  // CSVの取り込みの件数（古い記録の書き方も）。0件の項目は書かない（J-64・#343 L-5）
  const roster =
    /^登録(\d+)・更新(\d+)・エラー(\d+)$/.exec(text) ??
    /^登録 (\d+)件・更新 (\d+)件・取り込めない行 (\d+)件$/.exec(text);
  if (roster) {
    const parts = [
      roster[1] !== '0' ? `登録${roster[1]}件` : null,
      roster[2] !== '0' ? `更新${roster[2]}件` : null,
      roster[3] !== '0' ? `取り込めない行${roster[3]}件` : null,
    ].filter((part): part is string => part !== null);
    return parts.length > 0 ? parts.join('・') : '変わったところなし';
  }
  return withoutHonorificForAssistant(replaceIds(text, names)).replace(/: /g, '：');
}

/**
 * トークの呼び方（名前が無い1対1は「1対1のトーク」）。
 * 1対1は、サーバが参加者のお名前を返したときは「1対1のトーク（山田 太郎さん・AI先輩）」と
 * 誰の1対1かを添える（#334 L-15。夜間の補完で作った AI先輩との1対1など）。
 */
function roomLabel(
  target: AuditTarget,
  channelType: string | null | undefined,
  members?: { name: string; kind: string }[] | null,
): string {
  const name = (target.name ?? '').trim();
  // 名前の無いトーク（1対1）は、記録に種類の記号（dm）が入っていることがある
  if (channelType === 'dm' || name === '' || name === 'dm') {
    const who = (members ?? [])
      .filter((member) => member.name.trim() !== '')
      .map((member) => (member.kind === 'human' ? `${member.name}さん` : member.name));
    return who.length > 0 ? `1対1のトーク（${who.join('・')}）` : '1対1のトーク';
  }
  return `トーク「${name}」`;
}

/** 対象の欄に書くことが無いときの印。 */
const NO_TARGET = '—';

/** 操作した方ご自身が対象になる操作（ログイン）。対象の欄にお名前を繰り返さない。 */
const SELF_ACTIONS = new Set([
  'admin.signed_in',
  'app.signed_in',
  'invite.signed_in',
  'verification_token.signed_in',
  'store_review.signed_in',
  'user.activated',
  'user.signed_in_again',
]);

/** 1対1のトークか（呼び方の頭で見分ける）。 */
function isDirectRoom(room: string): boolean {
  return room.startsWith('1対1のトーク');
}

/** 「何に対して」を1行の文にする。 */
export function formatTarget(
  item: Pick<AuditLogItem, 'action' | 'target'> &
    Partial<
      Pick<
        AuditLogItem,
        | 'actorUserId'
        | 'targetUserId'
        | 'targetChannelType'
        | 'targetChannelMembers'
        | 'targetKnowledgeQuestion'
      >
    >,
  names?: Record<string, string>,
): string {
  const target = item.target;
  if (!target) return '';
  // ご自身のログインの行は、対象が操作した方と同じなので繰り返さない（#329-2）
  if (
    SELF_ACTIONS.has(item.action) &&
    item.actorUserId &&
    (item.targetUserId ?? target.id) === item.actorUserId
  ) {
    return NO_TARGET;
  }
  const hasChange = target.from !== undefined || target.to !== undefined;
  const from = target.from === undefined ? '' : String(target.from);
  const to = target.to === undefined ? '' : String(target.to);

  // ログインの上限（#336）。会社全体が対象なので、どの上限か・どう変えたかを1行で
  if (item.action === 'login.limit_reached') {
    const name = (target.name ?? '').trim() || 'ログインの上限';
    return to ? `${name}（上限 ${to}）` : name;
  }
  if (item.action === 'login.limit_raised') {
    return target.name ? `ログインの上限（${target.name}）` : 'ログインの上限';
  }
  if (item.action === 'login.counts_reset') {
    // 何を（会社全体／電話番号の下4桁）、何回分を0に戻したか（#368）
    const name = (target.name ?? '').trim() || 'ログインの数え';
    return from ? `${name}（${from} → 0）` : name;
  }
  if (item.action === 'verification_sms.viewed') {
    // どこで見たか（管理画面／サーバのコマンド）を添える。コマンドのときは「操作した方」が空（#358）
    const where = (target.name ?? '').trim();
    return where ? `認証番号の控え（${where}）` : '認証番号の控え';
  }
  if (item.action === 'login.limit_raise_cancelled') {
    return from
      ? `ログインの上限（${from} → いつもの上限）`
      : 'ログインの上限（いつもの上限に戻した）';
  }

  switch (target.type) {
    case 'user': {
      const who = personLabel(item.targetUserId ?? target.id, names);
      return hasChange ? `${who}（${change(from, to)}）` : who;
    }
    case 'invite':
      return `${personLabel(item.targetUserId, names)}への招待リンク`;
    case 'join_request': {
      // 断った申し込みは名簿に入っていないので、申し込みに書かれたお名前で出す
      const name = (target.name ?? '').trim();
      return name === '' ? '参加の申し込み' : `${name}さんの参加の申し込み`;
    }
    case 'channel': {
      const room = roomLabel(target, item.targetChannelType, item.targetChannelMembers);
      if (item.action === 'channel.created') {
        const kind = to ? channelTypeLabel(to) : '';
        return kind && !isDirectRoom(room) ? `${room}（${kind}）` : room;
      }
      if (!hasChange) return room;
      // トークのお一人ずつの既読・未読の取り出し（issue #534）。過去の記録のために残す。
      // どのメッセージかは送った日時で残っている（本文は残さない）
      if (item.action === 'read_receipts.exported' && !Number.isNaN(Date.parse(to))) {
        return `${room}（${formatDateTime(to)}に送られたメッセージ）`;
      }
      const automatic = describeAutoMembership(from, to);
      if (automatic !== null) return `${room}（${automatic}）`;
      if (from.includes('=') || to.includes('=')) {
        return `${room}（${describeFieldChanges(from, to, names)}）`;
      }
      if (from && to) {
        return `${room}（${describeNote(from, names)} → ${describeNote(to, names)}）`;
      }
      return `${room}（${describeNote(from || to, names)}）`;
    }
    case 'announcement': {
      const room = target.name ? `「${target.name}」` : '';
      if (item.action === 'announcement.repushed' && to) {
        return `お知らせ${room}（${to}回目の再通知）`;
      }
      return to ? `お知らせ${room}（宛先：${to}）` : `お知らせ${room}`;
    }
    case 'ai_knowledge': {
      const question = item.targetKnowledgeQuestion?.trim();
      const label = question ? `AI先輩の答え「${question}」` : 'AI先輩の答え';
      if (from && to) {
        return `${label}（${KNOWLEDGE_STATUS[from] ?? '承認待ち'} → ${KNOWLEDGE_STATUS[to] ?? '取り下げ済み'}）`;
      }
      const kind = KNOWLEDGE_KIND[to];
      return kind ? `${label}（${kind}）` : label;
    }
    case 'roster_import':
      return to ? `CSVの取り込み（${describeNote(to, names)}）` : 'CSVの取り込み';
    case 'export': {
      // 会社のデータの一括の取り出し（issue #114）。形式と、写真・ファイルを入れたかを添える
      const name = (target.name ?? '').trim() || '会社のデータ';
      // かっこを重ねないよう「：」でつなぐ（形式の名前に「（CSV）」が入るため）
      return to ? `${name}：${to}` : name;
    }
    case 'sticker': {
      const name = target.name ? `スタンプ「${target.name}」` : 'スタンプ';
      if (!hasChange) return name;
      // 別のセットへ移した（issue #492）。前と後はセットの名前
      if (item.action === 'sticker.moved') return `${name}（セット：${change(from, to)}）`;
      const numeric = /^\d+$/.test(from) && /^\d+$/.test(to);
      return `${name}（${numeric ? '並び順' : '名前'}：${change(from, to)}）`;
    }
    case 'sticker_set': {
      // 並べ替え（issue #492）は1行で、前と後の並び（名前を「・」でつないだもの）を出す
      if (item.action === 'sticker_set.reordered') {
        return hasChange
          ? `スタンプのセットの並び（${change(from, to)}）`
          : 'スタンプのセットの並び';
      }
      const name = target.name ? `スタンプのセット「${target.name}」` : 'スタンプのセット';
      return hasChange ? `${name}（名前：${change(from, to)}）` : name;
    }
    default: {
      const type = auditTargetTypeLabel(target.type);
      const name = target.name ? `「${target.name}」` : '';
      const head = `${type}${name}`;
      return hasChange ? `${head}（${replaceIds(change(from, to), names)}）` : head;
    }
  }
}
